import type { Embedder, VectorStore } from '@fim/futurex-vector';
import { scanRepo } from './scan.js';
import { chunkFile } from './chunk.js';
import { redactSecrets } from './redact.js';
import { extractSymbols } from './symbol-index.js';
import { buildImportGraph } from './imports.js';
import type { FileImports } from './imports.js';
import type { SymbolDef } from './symbols.js';
import type { Chunk, IndexStats } from './types.js';

const EMBED_BATCH = 64;

/** What {@link buildIndex} produces: the stats plus the extracted symbol defs (to persist). */
export interface IndexResult {
  readonly stats: IndexStats;
  readonly symbols: SymbolDef[];
  readonly imports: FileImports[];
}

/** Scan → chunk → redact → embed → upsert, and extract an AST symbol index. The
 * chunk text fed to the embedder is prefixed with the path so retrieval can key
 * on filenames too. */
export async function buildIndex(
  root: string,
  embedder: Embedder,
  store: VectorStore<Chunk>,
): Promise<IndexResult> {
  const files = scanRepo(root);
  const chunks: Chunk[] = [];
  const symbols: SymbolDef[] = [];
  let redacted = 0;

  for (const file of files) {
    for (const chunk of chunkFile(file.path, file.content)) {
      const r = redactSecrets(chunk.text);
      redacted += r.redacted;
      chunks.push(r.redacted > 0 ? { ...chunk, text: r.text } : chunk);
    }
    // AST symbols from the raw source. Signatures are declaration headers (names +
    // types), never initializer values, so redaction is unnecessary here.
    symbols.push(...extractSymbols(file.path, file.content));
  }

  await store.clear();
  for (let i = 0; i < chunks.length; i += EMBED_BATCH) {
    const batch = chunks.slice(i, i + EMBED_BATCH);
    const vectors = await embedder.embed(batch.map((c) => `${c.path}\n${c.text}`));
    await store.upsert(batch.map((c, j) => ({ id: c.id, vector: vectors[j] ?? [], payload: c })));
  }

  // Import graph: resolve each file's imports against the indexed set + flag broken links.
  const imports = buildImportGraph(files);
  let edges = 0;
  let broken = 0;
  for (const fi of imports) {
    for (const imp of fi.imports) {
      edges += 1;
      if (imp.kind === 'unresolved') broken += 1;
    }
  }

  return {
    stats: {
      files: files.length,
      chunks: chunks.length,
      redacted,
      symbols: symbols.length,
      imports: edges,
      brokenImports: broken,
    },
    symbols,
    imports,
  };
}
